import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import './AttendanceLog.css';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function formatTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('en-PK', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-PK', {
    weekday: 'short', day: 'numeric', month: 'short',
  });
}

function formatHours(minutes) {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

// Determine display status based on clock_in + late_minutes
function getDisplayStatus(record) {
  if (!record.clock_in) return 'absent';
  if (record.late_minutes > 0) return 'late';
  return 'present';
}

export default function AttendanceLog({ employee }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth]     = useState(new Date().getMonth() + 1);
  const [year, setYear]       = useState(new Date().getFullYear());

  useEffect(() => { loadData(); }, [month, year]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await api.getMyAttendance(month, year);
      setRecords(data.records || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2024; y--) years.push(y);

  const present = records.filter(r => r.clock_in && r.late_minutes === 0).length;
  const late    = records.filter(r => r.clock_in && r.late_minutes > 0).length;
  const absent  = records.filter(r => !r.clock_in).length;

  return (
    <div className="log-page">

      {/* Month/Year pills */}
      <div className="log-period-row">
        <div className="log-period-pills">
          {MONTHS.map((m, i) => (
            <button
              key={i}
              className={`log-month-pill ${month === i + 1 ? 'log-pill-active' : ''}`}
              onClick={() => setMonth(i + 1)}
            >
              {m.slice(0, 3)}
            </button>
          ))}
        </div>
        <div className="log-year-pills">
          {years.map(y => (
            <button
              key={y}
              className={`log-year-pill ${year === y ? 'log-pill-active' : ''}`}
              onClick={() => setYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="log-stats">
        <div className="log-stat green">
          <div className="ls-val">{present}</div>
          <div className="ls-label">Present</div>
        </div>
        <div className="log-stat amber">
          <div className="ls-val">{late}</div>
          <div className="ls-label">Late</div>
        </div>
        <div className="log-stat red">
          <div className="ls-val">{absent}</div>
          <div className="ls-label">Absent</div>
        </div>
      </div>

      <div className="log-section-label">
        Full Log — {MONTHS[month - 1]} {year}
      </div>

      {loading ? (
        <div className="log-loading">Loading…</div>
      ) : records.length === 0 ? (
        <div className="log-card">
          <div className="log-empty">No records for {MONTHS[month - 1]} {year}</div>
        </div>
      ) : (
        <div className="log-card">
          {records.map((r, i) => {
            const status  = getDisplayStatus(r);
            const isLate  = r.clock_in && r.late_minutes > 0;
            const ongoing = r.clock_in && !r.clock_out;

            const iconMap  = { present: { s: '✓', c: 'present' }, late: { s: '⏱', c: 'late' }, absent: { s: '✗', c: 'absent' } };
            const badgeMap = { present: { l: 'Present', c: 'bpresent' }, late: { l: 'Late', c: 'blate' }, absent: { l: 'Absent', c: 'babsent' } };

            const icon  = iconMap[status];
            const badge = badgeMap[status];

            return (
              <div key={i} className="log-item">
                <div className={`log-icon ${icon.c}`}>{icon.s}</div>
                <div className="log-info">
                  <div className="log-date">{formatDate(r.date)}</div>
                  <div className="log-detail">
                    <span>In: {formatTime(r.clock_in)}</span>
                    <span>Out: {ongoing ? '—' : formatTime(r.clock_out)}</span>
                    {isLate && <span className="late-tag">+{r.late_minutes}m</span>}
                  </div>
                  {isLate && (
                    <div className="log-late-info">
                      Late by {r.late_minutes} min
                      {r.late_minutes >= 180 ? ' · Full day deduction risk' :
                       r.late_minutes >= 60  ? ' · Half day risk' : ''}
                    </div>
                  )}
                </div>
                <div className="log-right">
                  <div className="log-hours">
                    {ongoing ? 'Ongoing' : formatHours(r.total_minutes)}
                  </div>
                  <span className={`badge ${badge.c}`}>{badge.l}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}